import React, { useState } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { createTransportation, getBuySellFormMode, getSimplifiedFormEnabled, getOrganizationBySlug } from 'wasp/client/operations';
import { SimplifiedSubmitForm, SubmissionModeSwitch } from '../components/SimplifiedSubmit';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { resolveLocationSlug, locationSectionPath } from '../../client/lib/locationPaths';
import {
  Car,
  MapPin,
  Clock,
  DollarSign,
  Phone,
  Mail,
  Save,
  AlertCircle,
  HelpCircle,
  Star,
  Users,
  Navigation,
  User,
  CheckCircle2,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';

interface TransportationFormData {
  title: string;
  description: string;
  listingType: 'OFFER' | 'REQUEST';
  routeFrom: string;
  routeTo: string;
  scheduleType: 'DAILY' | 'WEEKLY' | 'ONE_TIME' | 'FLEXIBLE';
  schedule: string;
  seats: string;
  price: string;
  vehicleType: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const STEPS = [
  { id: 1, label: 'Trip Type', icon: Car, description: 'Offering or requesting a ride?' },
  { id: 2, label: 'Route & Schedule', icon: MapPin, description: 'Where and when?' },
  { id: 3, label: 'Trip Details', icon: Clock, description: 'Vehicle and pricing' },
  { id: 4, label: 'Contact & Review', icon: User, description: 'Finalize and submit' }
];

const SCHEDULE_TYPES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'FLEXIBLE', label: 'Flexible' }
];

const VEHICLE_TYPES = ['Sedan', 'SUV', 'Van', 'Truck', 'Bus', 'Motorcycle', 'Other'];

const TRANSPORT_LISTING_CONFIG = {
  OFFER: {
    titlePlaceholder: 'e.g., Daily Commute to Downtown, Airport Ride, Weekend Carpool',
    descriptionPlaceholder: 'Describe the ride, pickup details, schedule, any requirements, etc.',
    detailsHeading: 'Ride Details',
    showVehicleType: true,
    showSeats: true,
    showPrice: true,
    priceLabel: 'Price per Ride (Optional)',
    pricePriceTooltip: 'Cost per seat or per trip. Leave empty if offering for free.',
    scheduleDetailsLabel: 'Schedule Details',
    routeFromLabel: 'From',
    routeToLabel: 'To',
  },
  REQUEST: {
    titlePlaceholder: 'e.g., Need Ride to Airport, Looking for Carpool to Work',
    descriptionPlaceholder: 'Describe where you need to go, how often, and any requirements.',
    detailsHeading: 'Ride Request Details',
    showVehicleType: false,
    showSeats: false,
    showPrice: false,
    priceLabel: '',
    pricePriceTooltip: '',
    scheduleDetailsLabel: 'When do you need a ride?',
    routeFromLabel: 'Pickup From',
    routeToLabel: 'Destination',
  },
} as const;

const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div className="relative inline-block">
      <div onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} className="cursor-help">
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

const StepIndicator = ({ currentStep }: { currentStep: number }) => (
  <div className="w-full mb-8">
    <div className="relative flex items-center justify-between">
      <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
        <div className="h-full bg-blue-500 transition-all duration-500 ease-out" style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }} />
      </div>
      {STEPS.map((step) => {
        const Icon = step.icon;
        const isDone = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        return (
          <div key={step.id} className="relative flex flex-col items-center z-10">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${isDone ? 'bg-blue-500 border-blue-500 text-white' : isCurrent ? 'bg-white border-blue-500 text-blue-600 shadow-md shadow-blue-100' : 'bg-white border-gray-200 text-gray-400'}`}>
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <span className={`mt-2 text-xs font-medium hidden sm:block ${isCurrent ? 'text-blue-600' : isDone ? 'text-blue-400' : 'text-gray-400'}`}>{step.label}</span>
          </div>
        );
      })}
    </div>
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-500">Step {currentStep} of {STEPS.length} — <span className="font-medium text-gray-700">{STEPS[currentStep - 1].description}</span></p>
    </div>
  </div>
);

export default function AddTransportationPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { slug } = useParams<{ slug?: string }>();
  const locSlug = resolveLocationSlug(routerLocation.pathname, slug, selectedLocation);
  const { data: org } = useQuery(getOrganizationBySlug, { slug: slug! }, { enabled: !!slug }) as { data: any };

  const createTransportationFn = useAction(createTransportation);
  const { data: formMode, isLoading: formModeLoading } = useQuery(getBuySellFormMode);
  const { data: simplifiedEnabled, isLoading: simplifiedEnabledLoading } = useQuery(getSimplifiedFormEnabled);

  const [currentStep, setCurrentStep] = useState(1);
  const [submissionMode, setSubmissionMode] = useState<'full' | 'simplified'>('simplified');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<TransportationFormData>({
    title: '',
    description: '',
    listingType: 'OFFER',
    routeFrom: '',
    routeTo: '',
    scheduleType: 'DAILY',
    schedule: '',
    seats: '',
    price: '',
    vehicleType: '',
    contactName: user?.fullName ?? '',
    contactPhone: (user as any)?.phoneNumber ?? '',
    contactEmail: user?.email ?? '',
    visibility: 'VERIFIED'
  });

  const handleChange = (field: keyof TransportationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!selectedLocation?.id) newErrors.location = 'No location selected';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createTransportationFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        organizationId: org?.id,
        listingType: formData.listingType,
        routeFrom: formData.routeFrom.trim(),
        routeTo: formData.routeTo.trim(),
        scheduleType: formData.scheduleType,
        schedule: formData.schedule.trim(),
        seats: formData.seats ? parseInt(formData.seats) : undefined,
        price: formData.price ? parseInt(formData.price) : undefined,
        vehicleType: formData.vehicleType,
        contactName: formData.contactName.trim(),
        contactPhone: formData.contactPhone.trim(),
        contactEmail: formData.contactEmail.trim(),
        visibility: formData.visibility,
        submissionType: 'STANDARD'
      });
      handleGoBack();
    } catch (error) {
      console.error('Error creating transportation item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    if (slug && routerLocation.pathname.startsWith('/org/')) {
      navigate(`/org/${slug}/transportation`);
      return;
    }
    navigate(locationSectionPath(locSlug, 'transportation'));
  };

  const locationName = selectedLocation?.name || 'No location selected';
  const showModeSwitch = !!simplifiedEnabled;
  const effectiveMode: 'full' | 'simplified' = showModeSwitch ? submissionMode : 'full';

  const handleSimplifiedSubmit = async (description: string) => {
    if (!selectedLocation?.id) return;
    setIsSubmitting(true);
    try {
      const shortTitle = description.length > 60 ? description.slice(0, 57) + '…' : description;
      await createTransportationFn({
        title: shortTitle, description, locationId: selectedLocation.id,
        listingType: 'OFFER', routeFrom: '', routeTo: '', scheduleType: 'FLEXIBLE',
        schedule: '', seats: undefined, price: undefined, vehicleType: '',
        contactName: formData.contactName, contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail, visibility: 'VERIFIED',
        submissionType: 'QUICK'
      });
      handleGoBack();
    } catch (error) {
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canAdvanceFrom1 = formData.title.trim().length > 0;
  const canAdvanceFrom2 = formData.routeFrom.trim().length > 0 && formData.routeTo.trim().length > 0;

  const canAdvance = () => {
    if (currentStep === 1) return canAdvanceFrom1;
    if (currentStep === 2) return canAdvanceFrom2;
    return true;
  };

  const handleNext = () => {
    if (currentStep < STEPS.length && canAdvance()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You must be logged in to post transportation.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Wizard form
  const WizardForm = (
    <DashboardLayout>
      {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
      <div className="w-full max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Car className="w-8 h-8 text-blue-600 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Post Transportation</h1>
            <p className="text-gray-500 text-sm">{selectedLocation?.name || 'No location selected'}</p>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Step Content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-[360px]">

          {/* Step 1: Trip Type */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">What are you posting?</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Offering a Ride */}
                <button
                  type="button"
                  onClick={() => handleChange('listingType', 'OFFER')}
                  className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                    formData.listingType === 'OFFER'
                      ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {formData.listingType === 'OFFER' && (
                    <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-green-500" />
                  )}
                  <div className="font-semibold text-gray-900 mb-1">Offering a Ride</div>
                  <div className="text-sm text-gray-500">I can give a ride</div>
                </button>

                {/* Need a Ride */}
                <button
                  type="button"
                  onClick={() => handleChange('listingType', 'REQUEST')}
                  className={`relative p-3 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                    formData.listingType === 'REQUEST'
                      ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {formData.listingType === 'REQUEST' && (
                    <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-blue-500" />
                  )}
                  <div className="font-semibold text-gray-900 mb-1">Need a Ride</div>
                  <div className="text-sm text-gray-500">I am looking for a ride</div>
                </button>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder={TRANSPORT_LISTING_CONFIG[formData.listingType].titlePlaceholder}
                />
                {!canAdvanceFrom1 && formData.title !== undefined && (
                  <p className="mt-1 text-xs text-gray-400">Required to continue</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Route & Schedule */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Route and schedule</h2>

              {/* Route From / To */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {TRANSPORT_LISTING_CONFIG[formData.listingType].routeFromLabel} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={formData.routeFrom}
                      onChange={(e) => handleChange('routeFrom', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Departure point"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {TRANSPORT_LISTING_CONFIG[formData.listingType].routeToLabel} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      value={formData.routeTo}
                      onChange={(e) => handleChange('routeTo', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Destination"
                    />
                  </div>
                </div>
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Schedule Type</label>
                <div className="flex flex-wrap gap-2">
                  {SCHEDULE_TYPES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => handleChange('scheduleType', s.value)}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                        formData.scheduleType === s.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {TRANSPORT_LISTING_CONFIG[formData.listingType].scheduleDetailsLabel}
                </label>
                <input
                  type="text"
                  value={formData.schedule}
                  onChange={(e) => handleChange('schedule', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="e.g., Every Monday at 8am"
                />
              </div>
            </div>
          )}

          {/* Step 3: Trip Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Vehicle and pricing</h2>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    handleChange('description', e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  rows={4}
                  maxLength={255}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none overflow-hidden"
                  placeholder={TRANSPORT_LISTING_CONFIG[formData.listingType].descriptionPlaceholder}
                />
                <p className="mt-1 text-xs text-gray-400 text-right">{formData.description.length}/255 characters</p>
              </div>

              {/* Vehicle Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
                <select
                  value={formData.vehicleType}
                  onChange={(e) => handleChange('vehicleType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                >
                  <option value="">Select type</option>
                  {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Seats */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seats Available</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      step="1"
                      min="1"
                      max="20"
                      value={formData.seats}
                      onChange={(e) => handleChange('seats', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price <span className="text-gray-400 font-normal">(per person, optional)</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      value={formData.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Contact & Review */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>

              <p className="text-sm text-blue-600 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                Pre-filled from your account — edit if needed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => handleChange('contactPhone', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleChange('contactEmail', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Email address"
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2 text-sm">
                <h3 className="font-semibold text-gray-800 mb-3">Summary</h3>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium text-gray-900">{formData.listingType === 'OFFER' ? 'Offering a Ride' : 'Need a Ride'}</span>
                </div>
                {(formData.routeFrom || formData.routeTo) && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Route</span>
                    <span className="font-medium text-gray-900 text-right max-w-[60%]">
                      {formData.routeFrom}{formData.routeFrom && formData.routeTo ? ' to ' : ''}{formData.routeTo}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Schedule</span>
                  <span className="font-medium text-gray-900">
                    {SCHEDULE_TYPES.find(s => s.value === formData.scheduleType)?.label ?? formData.scheduleType}
                    {formData.schedule ? ` — ${formData.schedule}` : ''}
                  </span>
                </div>
                {formData.vehicleType && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Vehicle</span>
                    <span className="font-medium text-gray-900">{formData.vehicleType}</span>
                  </div>
                )}
                {formData.seats && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Seats</span>
                    <span className="font-medium text-gray-900">{formData.seats}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Price</span>
                  <span className="font-medium text-gray-900">
                    {!formData.price || formData.price === '0' ? 'Free' : `$${formData.price} per person`}
                  </span>
                </div>
              </div>

              {/* Approval notice */}
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>All postings go through an approval process before being published.</span>
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.submit}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={currentStep === 1 ? handleGoBack : handleBack}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-colors font-medium text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canAdvance()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Submit
                </>
              )}
            </button>
          )}
        </div>

        {/* Dot indicators */}
        <div className="mt-4 flex justify-center gap-2">
          {STEPS.map((step) => (
            <div
              key={step.id}
              className={`rounded-full transition-all duration-300 ${
                step.id === currentStep ? 'w-4 h-2 bg-blue-500' : step.id < currentStep ? 'w-2 h-2 bg-blue-300' : 'w-2 h-2 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );

  if (formModeLoading || simplifiedEnabledLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (effectiveMode === 'simplified') {
    return (
      <DashboardLayout>
        <div className="overflow-y-auto h-full bg-gray-50">
          {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
          <SimplifiedSubmitForm isSubmitting={isSubmitting} onSubmit={handleSimplifiedSubmit} selectedLocationName={locationName} />
        </div>
      </DashboardLayout>
    );
  }

  if (formMode === 'wizard') {
    return WizardForm;
  }

  return (
    <DashboardLayout>
      {showModeSwitch && <SubmissionModeSwitch activeMode={submissionMode} onSelectMode={setSubmissionMode} />}
      <div className="flex h-full">
        <div className="flex-1 bg-card">
          <div className="flex h-full">
            {/* Form Content */}
            <div className="flex-1 p-6 pb-32 lg:pb-6">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <Car className="w-8 h-8 text-blue-600" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">Post Transportation</h1>
                    <p className="text-gray-600">Location: {selectedLocation?.name || 'No location selected'}</p>
                  </div>
                </div>
              </div>

              <form id="transportation-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <div className="space-y-6 mb-6">
                  {/* Listing Type */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">What are you posting?</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { value: 'OFFER', label: 'Offering a Ride', icon: Car, description: 'I have a car and can offer rides' },
                        { value: 'REQUEST', label: 'Looking for a Ride', icon: Navigation, description: 'I need transportation' }
                      ].map((type) => {
                        const IconComponent = type.icon;
                        return (
                          <label
                            key={type.value}
                            className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                              formData.listingType === type.value
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <input
                              type="radio"
                              name="listingType"
                              value={type.value}
                              checked={formData.listingType === type.value}
                              onChange={(e) => handleChange('listingType', e.target.value as 'OFFER' | 'REQUEST')}
                              className="sr-only"
                            />
                            <div className="flex items-center gap-3">
                              <IconComponent className={`w-6 h-6 ${formData.listingType === type.value ? 'text-blue-600' : 'text-gray-400'}`} />
                              <div>
                                <div className="font-medium text-gray-900">{type.label}</div>
                                <div className="text-sm text-gray-500">{type.description}</div>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {TRANSPORT_LISTING_CONFIG[formData.listingType].detailsHeading}
                    </h3>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder={TRANSPORT_LISTING_CONFIG[formData.listingType].titlePlaceholder}
                      required
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.title}
                      </p>
                    )}
                  </div>

                  {/* Route */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {TRANSPORT_LISTING_CONFIG[formData.listingType].routeFromLabel}
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.routeFrom}
                          onChange={(e) => handleChange('routeFrom', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Starting point"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        {TRANSPORT_LISTING_CONFIG[formData.listingType].routeToLabel}
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.routeTo}
                          onChange={(e) => handleChange('routeTo', e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                          placeholder="Destination"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                        errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                      }`}
                      placeholder={TRANSPORT_LISTING_CONFIG[formData.listingType].descriptionPlaceholder}
                      required
                    />
                    {errors.description && (
                      <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Detail fields */}
                {(() => {
                  const config = TRANSPORT_LISTING_CONFIG[formData.listingType];
                  return (
                    <div className="space-y-5">
                      {/* Schedule Type — 4 pills in a row */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Schedule Type</label>
                        <div className="grid grid-cols-4 gap-2">
                          {SCHEDULE_TYPES.map((schedule) => (
                            <label
                              key={schedule.value}
                              className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                                formData.scheduleType === schedule.value
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name="scheduleType"
                                value={schedule.value}
                                checked={formData.scheduleType === schedule.value}
                                onChange={(e) => handleChange('scheduleType', e.target.value as any)}
                                className="sr-only"
                              />
                              <div>
                                <div className={`font-medium text-sm ${formData.scheduleType === schedule.value ? 'text-blue-700' : 'text-gray-900'}`}>
                                  {schedule.label}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Schedule details + Vehicle + Seats + Price in a row */}
                      <div className={`grid gap-4 ${config.showVehicleType ? 'grid-cols-4' : 'grid-cols-1'}`}>
                        <div className={config.showVehicleType ? '' : 'col-span-1'}>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            <span className="flex items-center gap-2">
                              {config.scheduleDetailsLabel}
                              <Tooltip content="Specify time, day of week, or any other schedule details">
                                <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                              </Tooltip>
                            </span>
                          </label>
                          <input
                            type="text"
                            value={formData.schedule}
                            onChange={(e) => handleChange('schedule', e.target.value)}
                            className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                            placeholder="e.g., Mon-Fri 8am, Every Sunday 9am"
                          />
                        </div>
                        {config.showVehicleType && (
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Vehicle Type</label>
                            <select
                              value={formData.vehicleType}
                              onChange={(e) => handleChange('vehicleType', e.target.value)}
                              className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                            >
                              <option value="">Select type</option>
                              {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                          </div>
                        )}
                        {config.showSeats && (
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              <span className="flex items-center gap-2">
                                Seats Available
                                <Tooltip content="How many seats are available?">
                                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                </Tooltip>
                              </span>
                            </label>
                            <div className="relative">
                              <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={formData.seats}
                                onChange={(e) => handleChange('seats', e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                                placeholder="e.g., 3"
                              />
                            </div>
                          </div>
                        )}
                        {config.showPrice && (
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              <span className="flex items-center gap-2">
                                {config.priceLabel}
                                <Tooltip content={config.pricePriceTooltip}>
                                  <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                                </Tooltip>
                              </span>
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.price}
                                onChange={(e) => handleChange('price', e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Contact Information — 3 columns */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Contact Information</label>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="text"
                              value={formData.contactName}
                              onChange={(e) => handleChange('contactName', e.target.value)}
                              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                              placeholder="Full name"
                            />
                          </div>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="tel"
                              value={formData.contactPhone}
                              onChange={(e) => handleChange('contactPhone', e.target.value)}
                              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                              placeholder="Phone number"
                            />
                          </div>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                              type="email"
                              value={formData.contactEmail}
                              onChange={(e) => handleChange('contactEmail', e.target.value)}
                              className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                              placeholder="Email address"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {errors.submit && (
                  <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}
              </form>
            </div>

            {/* Right Sidebar */}
            <div className="hidden lg:flex w-80 border-l border-border flex-col">
              <div className="p-6 border-b border-border">
                <div className="flex flex-col gap-3">
                  <button
                    type="submit"
                    form="transportation-form"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.title.trim()}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Posting...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleGoBack}
                    className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>

              {/* Approval Notice */}
              <div className="p-6 border-b border-border">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>All postings go through an approval process before being published.</span>
                </div>
              </div>

              {/* Tips */}
              <div className="p-6 flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tips for Better Results</h3>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Be specific about pickup location and timing</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Mention any requirements (e.g., only female passengers)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Clarify cost sharing vs. full charge</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <span>Provide a reliable way to contact you</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Action Bar */}
      <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleGoBack}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="transportation-form"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : 'Post'}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}



